import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  MessageCircle,
  BarChart3,
  Calendar,
  Calculator,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { APP_VERSION } from "@/lib/version";

const NAV_ITEMS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/messages", label: "Messages", icon: MessageCircle },
  { to: "/calendrier", label: "Calendrier", icon: Calendar },
  { to: "/rapports", label: "Rapports", icon: BarChart3 },
  { to: "/simulation", label: "Simulation", icon: Calculator },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, adminOnly: true },
  { to: "/parametres", label: "Paramètres", icon: Settings },
  { to: "/aide", label: "Aide", icon: HelpCircle },
];

export default function Sidebar({ collapsed, onToggleCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { companyName } = useSettings();

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.to;
    if (item.match) return item.match.includes(location.pathname);
    return location.pathname.startsWith(item.to);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={cn(
          "hidden lg:flex h-screen sticky top-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-in-out",
          collapsed ? "w-[72px]" : "w-[260px]"
        )}
      >
        {/* Brand */}
        <div className={cn(
          "flex items-center gap-3 border-b border-sidebar-border px-4 py-4",
          collapsed && "justify-center px-2"
        )}>
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-sidebar-border bg-white shadow-soft">
            <img
              src="/sogi-logo.png"
              alt="SOGI"
              className="h-7 w-7 object-contain"
            />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1 animate-fade-in">
              <div className="font-heading truncate text-sm font-bold tracking-tight">
                {companyName || "SOGI SARL"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sidebar-muted">
                CRM Immobilier
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV_ITEMS.filter((it) => !it.adminOnly || user?.role === "admin").map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const button = (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                className={cn(
                  "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                  active
                    ? "bg-sidebar-active text-sidebar-active-foreground shadow-soft"
                    : "text-sidebar-foreground hover:bg-accent hover:text-accent-foreground",
                  collapsed && "justify-center px-2"
                )}
              >
                <Icon className={cn("h-[18px] w-[18px] flex-none", active ? "" : "text-sidebar-muted group-hover:text-foreground")} strokeWidth={active ? 2.5 : 2} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
            return collapsed ? (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              button
            );
          })}
        </nav>

        {/* Footer (user / collapse) */}
        <div className="border-t border-sidebar-border p-3">
          {!collapsed ? (
            <div className="space-y-2 animate-fade-in">
              <div className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2">
                <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-accent-navy text-xs font-bold text-white">
                  {(user?.email || "A")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-xs font-semibold">Admin</span>
                    <span
                      className="rounded-full bg-accent-navy/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-navy dark:bg-accent-gold/20 dark:text-accent-gold"
                      title={`Version ${APP_VERSION}`}
                    >
                      v{APP_VERSION}
                    </span>
                  </div>
                  <div className="truncate text-[10px] text-sidebar-muted">
                    {user?.email || "—"}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="sidebar-logout-btn"
                  className="h-7 w-7 flex-none"
                  title="Déconnexion"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </div>
              <button
                onClick={onToggleCollapsed}
                data-testid="sidebar-collapse-btn"
                className="flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-[11px] font-medium text-sidebar-muted hover:bg-muted/50 hover:text-foreground"
              >
                <ChevronLeft className="h-3 w-3" />
                Réduire
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={async () => {
                      await logout();
                      navigate("/login");
                    }}
                    data-testid="sidebar-logout-btn"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-muted/40 hover:bg-muted"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Déconnexion</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onToggleCollapsed}
                    data-testid="sidebar-expand-btn"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-sidebar-muted hover:bg-muted/50 hover:text-foreground"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Étendre</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
